import { Modal } from "@/components";
import { OrderStatus, OrderType } from "@/api/payments";
import { formatDate, ResourceName } from "@/utils";
import { ExpandMore } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { FC, useMemo, useState } from "react";
import { Loading } from "react-admin";
import { useNavigate, useParams } from "react-router-dom";
import {
  PaymentsTabFilters,
  useGetUserPayments,
} from "../../queries";
import {
  displayStatusChipColor,
  formatDisplayStatus,
  formatOrderStatus,
  formatOrderType,
  formatPurchaseKind,
} from "../../utils";

const ROWS_PER_PAGE = 10;

type SortField =
  | "createdAt"
  | "type"
  | "price"
  | "status"
  | "latestActivityAt";

type ColumnId = SortField | "product" | "error";

const COLUMNS: { id: ColumnId; label: string; sortable: boolean }[] = [
  { id: "createdAt", label: "Created", sortable: true },
  { id: "type", label: "Type", sortable: true },
  { id: "product", label: "Product", sortable: false },
  { id: "price", label: "Amount", sortable: true },
  { id: "status", label: "Status", sortable: true },
  { id: "latestActivityAt", label: "Last activity", sortable: true },
  { id: "error", label: "Error", sortable: false },
];

const PaymentsTab: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [filters, setFilters] = useState<PaymentsTabFilters>({});
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);

  const { payments, isPending } = useGetUserPayments({
    userId: id,
    page,
    perPage: ROWS_PER_PAGE,
    filters,
    sortBy: sortField,
    sortDirection,
  });

  const onSortClick = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
    setPage(0);
  };

  const activeFilterChips = useMemo(() => {
    const chips: { id: string; label: string; onDelete: () => void }[] = [];
    filters.statuses?.forEach((s) =>
      chips.push({
        id: `status:${s}`,
        label: formatOrderStatus(s),
        onDelete: () =>
          setFilters({
            ...filters,
            statuses: filters.statuses?.filter((v) => v !== s),
          }),
      }),
    );
    filters.types?.forEach((t) =>
      chips.push({
        id: `type:${t}`,
        label: formatOrderType(t),
        onDelete: () =>
          setFilters({
            ...filters,
            types: filters.types?.filter((v) => v !== t),
          }),
      }),
    );
    return chips;
  }, [filters]);

  if (isPending) return <Loading />;
  if (!payments) return null;

  return (
    <Stack width="100%" spacing={2}>
      <Modal
        title="Filters"
        isOpened={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        children={
          <PaymentsTabFilterForm
            initial={filters}
            onSubmit={(next) => {
              setFilters(next);
              setPage(0);
              setIsFiltersModalOpen(false);
            }}
            onCancel={() => setIsFiltersModalOpen(false)}
          />
        }
      />

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">User Purchases</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            variant="filled"
            label={`Sorted: ${sortField} (${sortDirection})`}
          />
          <Button
            size="small"
            endIcon={<ExpandMore />}
            onClick={() => setIsFiltersModalOpen(true)}
          >
            Filters
          </Button>
        </Stack>
      </Stack>

      {activeFilterChips.length > 0 && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          {activeFilterChips.map(({ id, label, onDelete }) => (
            <Chip
              key={id}
              size="small"
              variant="filled"
              label={label}
              onClick={onDelete}
              onDelete={onDelete}
            />
          ))}
          <Button
            size="small"
            variant="outlined"
            onClick={() => setFilters({})}
          >
            Clear All
          </Button>
        </Stack>
      )}

      {payments.meta.total ? (
        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell key={col.id}>
                    {col.sortable ? (
                      <TableSortLabel
                        active={sortField === col.id}
                        direction={sortField === col.id ? sortDirection : "asc"}
                        onClick={() => onSortClick(col.id as SortField)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.data.map((payment) => (
                <TableRow
                  hover
                  key={payment.orderId}
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate(`/${ResourceName.USER_PURCHASES}/${payment.orderId}/show`)
                  }
                >
                  <TableCell>
                    {formatDate(payment.createdAt, "MMM D, YYYY HH:mm")}
                  </TableCell>
                  <TableCell>
                    {formatPurchaseKind(payment.type, payment.isRecurringProduct)}
                  </TableCell>
                  <TableCell>{payment.productTitle ?? "—"}</TableCell>
                  <TableCell>{`$${(payment.price / 100).toFixed(2)}`}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={formatDisplayStatus(
                        payment.status,
                        payment.latestEventStatus,
                        payment.createdAt,
                        payment.type,
                      )}
                      color={displayStatusChipColor(
                        payment.status,
                        payment.latestEventStatus,
                        payment.createdAt,
                        payment.type,
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    {payment.latestActivityAt
                      ? formatDate(payment.latestActivityAt, "MMM D, HH:mm")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {payment.latestErrorCode
                      ? `${payment.latestErrorCode}${
                          payment.latestErrorMessage
                            ? `: ${payment.latestErrorMessage}`
                            : ""
                        }`
                      : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            page={page}
            count={payments.meta.total}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[]}
            onPageChange={(_, newPage) => setPage(newPage)}
          />
        </TableContainer>
      ) : (
        <Typography>No purchases</Typography>
      )}
    </Stack>
  );
};

// Local filter form for the per-user tab. Doesn't use useListContext (we're
// outside react-admin's List). The global PaymentsList uses its own modal in
// modals/payments-filter that DOES use useListContext.
const STATUS_OPTIONS = Object.values(OrderStatus);
const TYPE_OPTIONS = Object.values(OrderType);

const PaymentsTabFilterForm: FC<{
  initial: PaymentsTabFilters;
  onSubmit: (filters: PaymentsTabFilters) => void;
  onCancel: () => void;
}> = ({ initial, onSubmit, onCancel }) => {
  const [statuses, setStatuses] = useState<OrderStatus[]>(initial.statuses ?? []);
  const [types, setTypes] = useState<OrderType[]>(initial.types ?? []);

  const toggle = <T,>(
    current: T[],
    setter: (next: T[]) => void,
    value: T,
  ) => {
    setter(
      current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value],
    );
  };

  return (
    <Stack spacing={2}>
      <Divider sx={{ borderColor: "#333" }} />
      <FormGroup>
        <Typography variant="h6">Status</Typography>
        {STATUS_OPTIONS.map((s) => (
          <FormControlLabel
            key={s}
            label={formatOrderStatus(s)}
            labelPlacement="start"
            sx={{ ml: 0, justifyContent: "space-between" }}
            control={
              <Checkbox
                checked={statuses.includes(s)}
                onChange={() => toggle(statuses, setStatuses, s)}
              />
            }
          />
        ))}
      </FormGroup>

      <Divider sx={{ borderColor: "#333" }} />
      <FormGroup>
        <Typography variant="h6">Type</Typography>
        {TYPE_OPTIONS.map((t) => (
          <FormControlLabel
            key={t}
            label={formatOrderType(t)}
            labelPlacement="start"
            sx={{ ml: 0, justifyContent: "space-between" }}
            control={
              <Checkbox
                checked={types.includes(t)}
                onChange={() => toggle(types, setTypes, t)}
              />
            }
          />
        ))}
      </FormGroup>

      <Divider sx={{ borderColor: "#333" }} />
      <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          onClick={() => {
            setStatuses([]);
            setTypes([]);
          }}
        >
          Reset
        </Button>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() =>
              onSubmit({
                statuses: statuses.length ? statuses : undefined,
                types: types.length ? types : undefined,
              })
            }
          >
            Filter
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

export { PaymentsTab };
